// frontend/src/App.js
// Project:      Vision ANPR by Castle Traffic Ltd
// Author:       Castle Traffic Ltd
// Contact:      dev@castle-traffic.co.uk
// Created:      2025-05-19
// Updated:      2026-10-19 15:10 BST
//
// Description:
//   Main React entry point. Providers + routing. Global job-create overlay mounted on allowed pages only.
//   Launch branding gates initial auth bootstrap without changing routing/auth logic.
//
// AUD8-FE-STATE:
//   - AuthProvider now wraps tenant-scoped providers.
//   - ThemeProvider, AlertProvider, Router/Layout/page tree remount when authScopeKey changes.
//   - Prevents stale tenant 1/tenant 2 component state persisting after tenant switching.

import React from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";

import LoginPage from "./pages/LoginPage";
import DashboardPage from "./pages/DashboardPage";
import SystemPage from "./pages/SystemPage";
import UserManagementPage from "./pages/UserManagementPage";
import WidgetPage from "./pages/WidgetPage";
import ReportingPage from "./pages/ReportingPage";
import JobsPage from "./pages/JobsPage";
import TasksPage from "./pages/TasksPage";
import ClientsPage from "./pages/ClientsPage";
import ContactsPage from "./pages/ContactsPage";
import OrganisationalCalendar from "./pages/OrganisationalCalendar";
import MessagingPage from "./pages/MessagingPage";
import OpportunitiesPage from "./pages/OpportunitiesPage";
import QuotesPage from "./pages/QuotesPage";
import MonitorPage from "./pages/MonitorPage";
import WorkflowPage from "./pages/WorkflowPage";
import CamerasPage from "./pages/CamerasPage";

import ProtectedRoute from "./components/ProtectedRoute";
import Layout from "./components/Layout";

import { ThemeProvider } from "./context/ThemeContext.jsx";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { AlertProvider } from "./context/AlertContext";

import GlobalOverlays from "./components/GlobalOverlays";
import TaskModalHost from "./components/TaskModalHost.jsx";
import PwaStartupGate from "./components/PwaStartupGate.jsx";

const RoutedApp = () => {
  const { authScopeKey } = useAuth();

  return (
    <ThemeProvider key={`theme-${authScopeKey}`}>
      <AlertProvider key={`alerts-${authScopeKey}`}>
        <Router key={`router-${authScopeKey}`}>
          <Routes>
            <Route path="/login" element={<LoginPage />} />

            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <Layout key={`layout-dashboard-${authScopeKey}`}>
                    <DashboardPage key={`dashboard-${authScopeKey}`} />
                    <GlobalOverlays key={`overlays-dashboard-${authScopeKey}`} />
                  </Layout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/dashboard/system"
              element={
                <ProtectedRoute>
                  <Layout key={`layout-system-${authScopeKey}`}>
                    <SystemPage key={`system-${authScopeKey}`} />
                  </Layout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/dashboard/users"
              element={
                <ProtectedRoute>
                  <Layout key={`layout-users-${authScopeKey}`}>
                    <UserManagementPage key={`users-${authScopeKey}`} />
                    <GlobalOverlays key={`overlays-users-${authScopeKey}`} />
                  </Layout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/dashboard/widgets"
              element={
                <ProtectedRoute>
                  <Layout key={`layout-widgets-${authScopeKey}`}>
                    <WidgetPage key={`widgets-${authScopeKey}`} />
                  </Layout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/dashboard/reporting"
              element={
                <ProtectedRoute>
                  <Layout key={`layout-reporting-${authScopeKey}`}>
                    <ReportingPage key={`reporting-${authScopeKey}`} />
                    <GlobalOverlays key={`overlays-reporting-${authScopeKey}`} />
                  </Layout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/dashboard/messaging"
              element={
                <ProtectedRoute>
                  <Layout key={`layout-messaging-${authScopeKey}`}>
                    <MessagingPage key={`messaging-${authScopeKey}`} />
                    <GlobalOverlays key={`overlays-messaging-${authScopeKey}`} />
                  </Layout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/dashboard/opportunities"
              element={
                <ProtectedRoute>
                  <Layout key={`layout-opportunities-${authScopeKey}`}>
                    <OpportunitiesPage key={`opportunities-${authScopeKey}`} />
                    <GlobalOverlays key={`overlays-opportunities-${authScopeKey}`} />
                  </Layout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/dashboard/quotes"
              element={
                <ProtectedRoute>
                  <Layout key={`layout-quotes-${authScopeKey}`}>
                    <QuotesPage key={`quotes-${authScopeKey}`} />
                    <GlobalOverlays key={`overlays-quotes-${authScopeKey}`} />
                  </Layout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/dashboard/tasks"
              element={
                <ProtectedRoute>
                  <Layout key={`layout-tasks-${authScopeKey}`}>
                    <TasksPage key={`tasks-${authScopeKey}`} />
                    <GlobalOverlays key={`overlays-tasks-${authScopeKey}`} />
                  </Layout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/dashboard/jobs"
              element={
                <ProtectedRoute>
                  <Layout key={`layout-jobs-${authScopeKey}`}>
                    <JobsPage key={`jobs-${authScopeKey}`} />
                    <GlobalOverlays key={`overlays-jobs-${authScopeKey}`} />
                  </Layout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/dashboard/monitor"
              element={
                <ProtectedRoute>
                  <Layout key={`layout-monitor-${authScopeKey}`}>
                    <MonitorPage key={`monitor-${authScopeKey}`} />
                    <GlobalOverlays key={`overlays-monitor-${authScopeKey}`} />
                  </Layout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/dashboard/cameras"
              element={
                <ProtectedRoute>
                  <Layout key={`layout-cameras-${authScopeKey}`}>
                    <CamerasPage key={`cameras-${authScopeKey}`} />
                    <GlobalOverlays key={`overlays-cameras-${authScopeKey}`} />
                  </Layout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/dashboard/workflows"
              element={
                <ProtectedRoute>
                  <Layout key={`layout-workflows-${authScopeKey}`}>
                    <WorkflowPage key={`workflows-${authScopeKey}`} />
                  </Layout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/dashboard/clients"
              element={
                <ProtectedRoute>
                  <Layout key={`layout-clients-${authScopeKey}`}>
                    <ClientsPage key={`clients-${authScopeKey}`} />
                    <GlobalOverlays key={`overlays-clients-${authScopeKey}`} />
                  </Layout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/dashboard/contacts"
              element={
                <ProtectedRoute>
                  <Layout key={`layout-contacts-${authScopeKey}`}>
                    <ContactsPage key={`contacts-${authScopeKey}`} />
                    <GlobalOverlays key={`overlays-contacts-${authScopeKey}`} />
                  </Layout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/dashboard/calendar"
              element={
                <ProtectedRoute>
                  <Layout key={`layout-calendar-${authScopeKey}`}>
                    <OrganisationalCalendar key={`calendar-${authScopeKey}`} />
                    <GlobalOverlays key={`overlays-calendar-${authScopeKey}`} />
                  </Layout>
                </ProtectedRoute>
              }
            />

            <Route path="/calendar" element={<Navigate to="/dashboard/calendar" replace />} />
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
          </Routes>
          <TaskModalHost />
        </Router>
      </AlertProvider>
    </ThemeProvider>
  );
};

function App() {
  return (
    <AuthProvider>
      <PwaStartupGate>
        <RoutedApp />
      </PwaStartupGate>
    </AuthProvider>
  );
}

export default App;